'use client';

import { Component, ReactNode } from 'react';
import { clientLogger } from '@/lib/client-logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Ignore DOM manipulation errors in development
    if (error.message.includes('Node.removeChild') || error.message.includes('removeChild')) {
      clientLogger.warn('Ignoring React DOM manipulation error (development only):', { detail: error.message });
      return { hasError: false };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Only log serious errors, ignore DOM manipulation warnings
    if (!error.message.includes('Node.removeChild') && !error.message.includes('removeChild')) {
      clientLogger.error(error, {
        componentStack: errorInfo?.componentStack || 'No component stack available',
      });
    } else {
      clientLogger.warn('ErrorBoundary ignored React DOM manipulation error (development only):', { detail: error.message });
    }
  }

  componentWillUnmount() {
    // Clean up any pending state updates
    this.setState = () => {};
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-6 max-w-2xl mx-auto">
          <div className="bg-red-950/20 border border-red-900 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-400 mb-3">
              Something went wrong
            </h2>
            <p className="text-red-300 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: undefined });
                window.location.reload();
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-white font-medium transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
